import * as React from "react";

// MUI
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

// Internal
import Logo from "../../../assets/logo-primary.png";
import en from "../../../data/en.json";
import { NavItem, NavLink, navigateTo } from "./navItems";

const drawerWidth = 280;

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  accountItems: NavLink[];
  isAuthenticated: boolean;
  onLogin: () => void;
  onLogout: () => void;
}

/** Opens the group holding the page we are already on, so it is not hidden. */
function initialExpandedGroups(items: NavItem[]): Record<string, boolean> {
  const pathname = window.location.pathname;
  const expanded: Record<string, boolean> = {};

  items.forEach((item) => {
    if (item.kind !== "group") return;
    expanded[item.id] = item.children.some(
      (child) => child.href.split("#")[0] === pathname
    );
  });

  return expanded;
}

export default function MobileNavDrawer(props: MobileNavDrawerProps) {
  const { open, onClose, items, accountItems, isAuthenticated } = props;

  const [expanded, setExpanded] = React.useState<Record<string, boolean>>(() =>
    initialExpandedGroups(items)
  );

  const toggleGroup = (id: string) =>
    setExpanded((previous) => ({ ...previous, [id]: !previous[id] }));

  const handleNavigate = (item: NavLink) => {
    onClose();
    navigateTo(item);
  };

  const renderLink = (item: NavLink, nested = false) => (
    <ListItem key={item.id} disablePadding>
      <ListItemButton
        onClick={() => handleNavigate(item)}
        sx={nested ? { pl: 4 } : undefined}
      >
        <ListItemText primary={item.label} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box component="img" sx={{ height: 24 }} alt="logo" src={Logo} />
          <Link
            variant="h6"
            underline="none"
            href="/"
            sx={{ pl: 2, fontSize: 20, color: "primary.main" }}
          >
            {en.common.inkBowl}
          </Link>
        </Box>
        <IconButton aria-label={en.common.close} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <List>
        {items.map((item) => {
          if (item.kind === "link") return renderLink(item);

          const isExpanded = Boolean(expanded[item.id]);
          const listId = `mobile-nav-group-${item.id}`;

          return (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  aria-expanded={isExpanded}
                  aria-controls={listId}
                  onClick={() => toggleGroup(item.id)}
                >
                  <ListItemText primary={item.label} />
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List id={listId} component="div" disablePadding>
                  {item.children.map((child) => renderLink(child, true))}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>

      <Divider />

      <List>
        {accountItems.map((item) => renderLink(item))}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              onClose();
              if (isAuthenticated) {
                props.onLogout();
              } else {
                props.onLogin();
              }
            }}
          >
            <ListItemText
              primary={isAuthenticated ? en.common.logout : en.common.login}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
}
